import React, { createRef, useState } from 'react';

import { Moment } from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useMediaQuery, useTheme } from '@material-ui/core';

import { sendBulkMessage } from '../../contacts/actions/send-bulk-message';
import { sendText } from '../../contacts/actions/send-text';
import { employeeContextSelector } from '../../reducers/contact-selectors';

import { EmployeeTextMessageList } from '../../employees/employee-text-message-list';
import { ProductTelemetryController } from '../../product-telemetry/product-telemetry-controller';
import { EmployeeMessageComposer } from '../message-composer/employee-message-composer';
import { EmployeeMessageTemplatesDialog } from '../message-composer/employee-message-templates-dialog';

import { EmployeeCanMessage } from '../../util/can-message';
import { MESSAGE_TYPE } from '../../util/message-types';
import { TEMPLATE_TYPE } from '../../util/template-type';
import { EmployeeMessageBanners } from '../widgets/employee-message-banners';

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    sendMessage: (
      contact: { contactId: number; context: ContactContextType },
      messageBody: string,
      messageType: string,
      subject?: string | null,
      sendDateTime?: Moment | null,
      urlMappings?: Record<string, string>,
      sendVCardForOrgId?: number,
    ) =>
      dispatch(
        sendText(
          contact.contactId,
          contact.context,
          messageBody,
          messageType,
          subject,
          { orgId },
          sendDateTime,
          urlMappings,
          sendVCardForOrgId,
        ),
      ),
    sendBulkMessage: (
      contacts: Array<{ contactId: number; context: ContactContextType }>,
      messageBody: string,
      messageType: string,
      hasVCardAttached: boolean,
      subject?: string | null,
      sendDateTime?: Moment | null,
      urlMappings?: Record<string, string>,
    ) =>
      dispatch(
        sendBulkMessage(
          contacts,
          messageBody,
          messageType,
          hasVCardAttached,
          subject,
          { orgId },
          sendDateTime,
          urlMappings,
        ),
      ),
  },
});

type InputProps = {
  orgId: number;
  onMessageSent?: (employeeId: number) => void;
  onClose: () => void;
  selectedEmployees: Employee[];
  initialMessage?: string;
};

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

export const NewEmployeeMessageComposer = connect(
  null,
  mapDispatchToProps,
)(({ actions, onMessageSent, orgId, onClose, selectedEmployees, initialMessage }: Props) => {
  const [messageType, setMessageType] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(initialMessage || null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState<boolean>(false);

  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const messageComposerRef: React.RefObject<HTMLDivElement> = createRef();

  const handleMessageTypeChange = (type: string) => {
    setMessageType(type);
  };

  const handleSendMessage = async (
    messageBody: string,
    type: string,
    subject?: string | null,
    sendDateTime?: Moment | null,
    optionalFields?: { urlFileMappings?: Record<string, string>; sendWithVCard?: boolean },
  ) => {
    const sendVCardForOrgId = optionalFields?.sendWithVCard ? orgId : undefined;
    const contacts = selectedEmployees
      .filter((e) => {
        const canMessage = new EmployeeCanMessage(e);
        if (messageType === MESSAGE_TYPE.TEXT) {
          return canMessage.canReceive(MESSAGE_TYPE.TEXT);
        }
        return canMessage.canReceive(MESSAGE_TYPE.EMAIL);
      })
      .map((e) => ({
        contactId: e.contactId,
        context: employeeContextSelector(e.employeeId),
      }));
    if (contacts.length === 0) {
      return {
        errorMessage: 'No employees would receive this message',
      };
    }

    ProductTelemetryController.logEvent('clicked send employee message', {
      value: contacts.map((c) => c.contactId).join(','),
      metadata: {
        isOnMobile: isOnMobile ? 'true' : 'false',
        type,
        isScheduled: sendDateTime ? 'true' : 'false',
      },
    });

    let response;
    const [firstContact] = contacts;

    if (contacts.length === 1) {
      response = await actions.sendMessage(
        firstContact,
        messageBody,
        type,
        subject,
        sendDateTime,
        optionalFields?.urlFileMappings,
        sendVCardForOrgId,
      );
    } else {
      // We pull the orgId/vCardPath when we hit the sendMessage method. Because these messages get scheduled, unlike the single message above,
      // we will store that they should have a contact card sent and grab the contact card later
      const hasVCardAttached = !!sendVCardForOrgId;
      response = await actions.sendBulkMessage(
        contacts,
        messageBody,
        type,
        hasVCardAttached,
        subject,
        sendDateTime,
        optionalFields?.urlFileMappings,
      );
    }

    if (onMessageSent && firstContact.context.employeeId) {
      onMessageSent(firstContact.context.employeeId);
    }

    return response;
  };

  const insertTemplate = (template: string) => {
    setMessage(template);
  };

  // IF (07/07/20): It's possible that the message composer gains focus and then the messages finish
  // loading - this pushes the composer out of view and starts the user at the top of the message
  // thread. That's frustrating for long threads, so we scroll the composer back into view when the
  // messages load.
  const handleMessageThreadLoaded = () => {
    messageComposerRef.current?.scrollIntoView(false);
  };

  let selectedEmployeeId = null;
  let selectedEmployee = null;
  let canMessage = null;
  let enableText = true;
  let enableEmail = true;
  if (selectedEmployees.length === 1) {
    [selectedEmployee] = selectedEmployees;
    selectedEmployeeId = selectedEmployee.employeeId;
    canMessage = new EmployeeCanMessage(selectedEmployee);
    enableText = canMessage.canReceive(MESSAGE_TYPE.TEXT);
    enableEmail = canMessage.canReceive(MESSAGE_TYPE.EMAIL);
  }

  const handleToggleTemplateDialogVisible = () => {
    setTemplateDialogOpen(!templateDialogOpen);
  };

  return (
    <div className="u-width-100 u-maxWidth-800px u-margin-0Auto">
      <div className="u-display-flex u-flexDirection-column u-height-100 u-overflow-auto">
        <div className="u-height-100 u-overflow-auto u-padding-xl">
          <div>
            {selectedEmployeeId ? (
              <EmployeeTextMessageList
                employeeId={selectedEmployeeId}
                onMessagesLoaded={handleMessageThreadLoaded}
              />
            ) : null}
            {selectedEmployee && (
              <EmployeeMessageBanners
                canMessage={canMessage}
                employee={selectedEmployee}
                messageInputHidden={!enableText && !enableEmail}
              />
            )}
            <div ref={messageComposerRef} className="u-margin-top-sm">
              <EmployeeMessageComposer
                orgId={orgId}
                message={message}
                enableText={enableText}
                enableEmail={enableEmail}
                initialType={enableEmail && !enableText ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT}
                handleToggleTemplateDialog={handleToggleTemplateDialogVisible}
                onMessageTypeChange={handleMessageTypeChange}
                handleTemplateSelect={insertTemplate}
                handleSendMessage={handleSendMessage}
                onClose={onClose}
              />
            </div>
            {(enableText || enableEmail) && templateDialogOpen && (
              <EmployeeMessageTemplatesDialog
                employeeId={selectedEmployeeId}
                orgId={orgId}
                insertTemplate={insertTemplate}
                templateType={TEMPLATE_TYPE.MESSAGE}
                messageType={messageType}
                onClose={handleToggleTemplateDialogVisible}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
